import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  {
    to: "/services",
    label: "Services",
    dropdown: [
      { to: "/services/digital-sales-engine", label: "Digital Sales Engine" },
      { to: "/services/personal-branding", label: "Personal Branding" },
    ],
  },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#E4E4E7] shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
          : "bg-white"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-44 md:h-52 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/assets/logo_on_light.png" alt="BrandChilly" className="h-40 md:h-48 w-auto" />
        </Link>

        <ul className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            l.dropdown ? (
              <li key={l.to} className="relative group">
                <div className="flex">
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `pl-4 pr-2 py-2 text-sm tracking-wide rounded-l-full transition-all flex items-center gap-1 ${
                        isActive || l.dropdown.some((d) => location.pathname.startsWith(d.to))
                          ? "text-white bg-white/5 border border-white/10 border-r-0"
                          : "text-zinc-400 hover:text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                  <div className={`pr-3 py-2 text-sm rounded-r-full flex items-center transition-all ${
                    location.pathname.startsWith("/services") ? "text-white bg-white/5 border border-white/10 border-l-0" : "text-zinc-400 group-hover:text-white"
                  }`}>
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </div>
                </div>
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all min-w-[240px]" data-testid={`nav-dropdown-${l.label.toLowerCase()}`}>
                  <div className="bg-black/95 backdrop-blur-xl border border-white/10 py-2" style={{ borderRadius: 0 }}>
                    {l.dropdown.map((d) => (
                      <NavLink
                        key={d.to}
                        to={d.to}
                        data-testid={`nav-dropdown-item-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `block px-5 py-2.5 text-sm transition-colors ${
                            isActive ? "text-red-400" : "text-zinc-300 hover:text-white hover:bg-white/[0.03]"
                          }`
                        }
                      >
                        {d.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </li>
            ) : (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm tracking-wide rounded-full transition-all ${
                      isActive
                        ? "text-white bg-white/5 border border-white/10"
                        : "text-zinc-400 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            )
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <Link
            to="/book"
            data-testid="navbar-book-call-btn"
            className="hidden md:inline-flex btn-primary text-sm py-2.5 px-5"
          >
            Book a Call <ArrowUpRight size={16} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 bg-white border-t border-[#E4E4E7] ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <ul className="flex flex-col px-6 py-4 gap-1">
          {links.map((l) => (
            l.dropdown ? (
              <li key={l.to}>
                <button
                  onClick={() => setServicesOpen((v) => !v)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-toggle`}
                  className="w-full flex items-center justify-between py-3 text-base tracking-wide text-zinc-300"
                >
                  <span>{l.label}</span>
                  <ChevronDown size={16} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all ${servicesOpen ? "max-h-40" : "max-h-0"}`}>
                  <NavLink to={l.to} className="block py-2 pl-4 text-sm text-zinc-500 hover:text-white">Services overview</NavLink>
                  {l.dropdown.map((d) => (
                    <NavLink
                      key={d.to}
                      to={d.to}
                      data-testid={`mobile-nav-dropdown-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => `block py-2 pl-4 text-sm ${isActive ? "text-red-400" : "text-zinc-400 hover:text-white"}`}
                    >
                      {d.label}
                    </NavLink>
                  ))}
                </div>
              </li>
            ) : (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-3 text-base tracking-wide ${isActive ? "text-white" : "text-zinc-400"}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            )
          ))}
          <li className="pt-3">
            <Link
              to="/book"
              className="btn-primary w-full justify-center text-sm"
              data-testid="mobile-book-call-btn"
            >
              Book a Call <ArrowUpRight size={16} />
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
}
